import Dialog from 'global/guides/Dialog'
import { CheckIcon, XIcon } from 'lucide-react'

interface AvisoFotoFacialProps {
    open: boolean
    onCancel: () => void
    onConfirm: () => void
}

/**
 * Aviso exibido antes de abrir o seletor de arquivos da foto do funcionário.
 * A foto alimenta o reconhecimento facial do registro de ponto — o enquadramento
 * exigido é diferente do de uma foto de rede social, e a recusa do enroll só
 * apareceria depois do upload.
 */
export function AvisoFotoFacial({
    open,
    onCancel,
    onConfirm,
}: AvisoFotoFacialProps) {
    return (
        <Dialog.Confirmar
            open={open}
            onClose={onCancel}
            onConfirm={onConfirm}
            title="Como deve ser a foto"
            confirmar="Entendi, escolher foto"
            cancelar="Cancelar"
            width="md"
            fecharAoConfirmar={false}
        >
            <p className="text-sm">
                A foto é usada pelo reconhecimento facial no registro de ponto.
                Não é o mesmo tipo de foto que se usa em rede social.
            </p>

            <div className="my-4 flex justify-between gap-3">
                <Exemplo
                    variante="correto"
                    rotulo="Correto"
                    legenda="Rosto centralizado"
                />
                <Exemplo
                    variante="distante"
                    rotulo="Evite"
                    legenda="Rosto pequeno ou fora do centro"
                />
                <Exemplo
                    variante="adereco"
                    rotulo="Não utilize"
                    legenda="Óculos escuros ou boné"
                />
            </div>

            <ul className="flex flex-col gap-1 text-sm">
                <Requisito permitido>
                    Rosto centralizado, ocupando boa parte da imagem
                </Requisito>
                <Requisito permitido>
                    Olhando para a frente, com expressão neutra
                </Requisito>
                <Requisito permitido>
                    Fundo claro e uniforme, boa iluminação, sem sombra no rosto
                </Requisito>
                <Requisito>Óculos escuros, boné, chapéu ou máscara</Requisito>
                <Requisito>Outras pessoas na foto</Requisito>
                <Requisito>Foto com filtro, recorte ou montagem</Requisito>
            </ul>
        </Dialog.Confirmar>
    )
}

interface RequisitoProps {
    permitido?: boolean
    children: React.ReactNode
}

function Requisito({ permitido, children }: RequisitoProps) {
    const Icone = permitido ? CheckIcon : XIcon

    return (
        <li className="flex items-start gap-2">
            <Icone
                className={`mt-0.5 size-4 shrink-0 ${
                    permitido ? 'text-success' : 'text-error'
                }`}
            />
            <span>
                <span className="sr-only">
                    {permitido ? 'Necessário: ' : 'Não pode: '}
                </span>
                {children}
            </span>
        </li>
    )
}

type Variante = 'correto' | 'distante' | 'adereco'

interface ExemploProps {
    variante: Variante
    rotulo: string
    legenda: string
}

function Exemplo({ variante, rotulo, legenda }: ExemploProps) {
    const correto = variante === 'correto'
    const cor = correto ? 'text-success' : 'text-error'

    return (
        <figure className={`flex flex-1 flex-col items-center gap-1 ${cor}`}>
            <svg
                viewBox="0 0 80 80"
                aria-hidden="true"
                className={`aspect-square w-full rounded-md border-2 ${
                    correto ? 'border-success' : 'border-error'
                }`}
            >
                <Silhueta variante={variante} />
            </svg>
            <figcaption className="text-center text-xs leading-tight">
                <span className="block font-semibold">{rotulo}</span>
                {legenda}
            </figcaption>
        </figure>
    )
}

const traco = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 3,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
}

function Silhueta({ variante }: { variante: Variante }) {
    if (variante === 'distante')
        return (
            <g {...traco}>
                <circle cx="53" cy="28" r="7" />
                <path d="M42 50c1.4-5.2 5.6-8.6 11-8.6s9.6 3.4 11 8.6" />
            </g>
        )

    if (variante === 'adereco')
        return (
            <g>
                <g {...traco}>
                    <circle cx="40" cy="38" r="18" />
                    <path d="M34 47c2 2 10 2 12 0" />
                    <path d="M12 78c3-13 13-21 28-21s25 8 28 21" />
                </g>
                {/* boné: copa sobre o topo da cabeça e viseira apontando para o lado */}
                <path
                    d="M24 31a16 16 0 0 1 32 0z"
                    fill="currentColor"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinejoin="round"
                />
                <ellipse
                    cx="17"
                    cy="31"
                    rx="9"
                    ry="3.5"
                    fill="currentColor"
                    transform="rotate(-10 17 31)"
                />
                {/* óculos escuros: duas lentes cheias ligadas pela ponte */}
                <rect
                    x="26"
                    y="34"
                    width="11"
                    height="8"
                    rx="2.5"
                    fill="currentColor"
                />
                <rect
                    x="43"
                    y="34"
                    width="11"
                    height="8"
                    rx="2.5"
                    fill="currentColor"
                />
                <path d="M37 37h6" {...traco} />
            </g>
        )

    return (
        <g {...traco}>
            <circle cx="40" cy="34" r="19" />
            <circle cx="33" cy="32" r="1.5" fill="currentColor" />
            <circle cx="47" cy="32" r="1.5" fill="currentColor" />
            <path d="M34 42c2 2 10 2 12 0" />
            <path d="M12 78c3-14 14-22 28-22s25 8 28 22" />
        </g>
    )
}
